// The game connection (design 4 §3.1, design 7 §6.1): one WebSocket per tab carrying binary
// mj.protocol.v1 messages. On every (re)connection the client says Hello; the server answers Welcome and,
// when the account is seated, a Resync. The client answers each shuffle Commitment with a nonce (design 2
// §3.2: fresh per commitment, the same one if the commitment comes again) and keeps what it needs to
// verify the game later (game/proofs); Pings keep the link alive. A dropped socket reconnects with
// backoff. A link can also die without closing (a phone changing networks): one that has said nothing for
// SILENT_MS is dropped and reopened, one the browser knows is offline at once, and when the browser comes
// back online or the tab comes back into view, a closed link reopens at once and an open one is probed
// (design 8 item 7).

import { create, fromBinary, toBinary } from "@bufbuild/protobuf";
import type { MessageInitShape } from "@bufbuild/protobuf";
import type { Action, ActionType, HandSlots } from "../pb/mj/record/v1/record_pb";
import { ClientMessageSchema, ErrorCode, ServerMessageSchema } from "../pb/mj/protocol/v1/protocol_pb";
import type { ServerMessage } from "../pb/mj/protocol/v1/protocol_pb";
import { proofs } from "../game/proofs";

export const PROTOCOL_VERSION = 1;
export const PING_MS = 5_000;
export const SILENT_MS = 15_000; // pings each answered: two missed answers and a margin. A turn's clock is 10 s and its bank 20 s: a player must know in time
export const PROBE_MS = 6_000; // after waking, an answer must come this soon
export const CLIENT_BUILD = `web-${import.meta.env.MODE}`;

// superseded: another tab of this account took the game; this one waits until the player asks for it back.
export type Status = "connecting" | "open" | "closed" | "signed-out" | "superseded";

export interface Handlers {
  message(m: ServerMessage): void;
  status(s: Status): void;
}

type Body = NonNullable<MessageInitShape<typeof ClientMessageSchema>["body"]>;

export class GameSocket {
  private ws: WebSocket | null = null;
  private stopped = false;
  private retries = 0;
  private ping: ReturnType<typeof setInterval> | undefined;
  private watch: ReturnType<typeof setInterval> | undefined;
  private retry: ReturnType<typeof setTimeout> | undefined;
  private heard = 0; // when the server last said anything
  private probe = 0; // when a probe went out that is not yet answered (0: none)
  private gameId = "";
  private readonly wake = () => {
    if (this.stopped || globalThis.document?.visibilityState === "hidden") return;
    if (this.ws === null) {
      clearTimeout(this.retry);
      this.retries = 0;
      this.open();
    } else if (this.ws.readyState === WebSocket.OPEN && this.probe === 0) {
      this.probe = Date.now();
      this.send({ case: "ping", value: { t: BigInt(Date.now()) } });
    }
  };

  private readonly offline = () => {
    if (this.ws !== null) this.lost(this.ws);
  };

  constructor(private readonly handlers: Handlers, private readonly url = socketURL()) {}

  start(): void {
    this.stopped = false;
    globalThis.window?.addEventListener("online", this.wake);
    globalThis.window?.addEventListener("offline", this.offline);
    globalThis.document?.addEventListener("visibilitychange", this.wake);
    this.open();
  }

  stop(): void {
    this.stopped = true;
    globalThis.window?.removeEventListener("online", this.wake);
    globalThis.window?.removeEventListener("offline", this.offline);
    globalThis.document?.removeEventListener("visibilitychange", this.wake);
    clearInterval(this.ping);
    clearInterval(this.watch);
    clearTimeout(this.retry);
    this.ws?.close();
    this.ws = null;
  }

  act(gameId: string, promptId: number, action: Action, slots?: HandSlots, callSlots: number[] = []): void {
    this.send({ case: "act", value: { gameId, promptId, action, slots, callSlots } });
    // A press on a link that has quietly died would wait out SILENT_MS looking accepted: an answer must come soon.
    if (this.ws?.readyState === WebSocket.OPEN && this.probe === 0) {
      this.probe = Date.now();
      this.send({ case: "ping", value: { t: BigInt(Date.now()) } });
    }
  }

  leave(gameId: string): void {
    this.send({ case: "leave", value: { gameId } });
  }

  /** The hand's result is shown (done false), then closed (done true): the next hand waits for it (at most 15 s). */
  press(gameId: string, action: ActionType, seenSeq: number): void {
    this.send({ case: "press", value: { gameId, action, seenSeq } });
  }

  reading(gameId: string, handIndex: number, done: boolean): void {
    this.send({ case: "reading", value: { gameId, handIndex, done } });
  }

  private open(): void {
    this.handlers.status("connecting");
    const ws = new WebSocket(this.url);
    ws.binaryType = "arraybuffer";
    this.ws = ws;
    ws.onopen = () => {
      this.retries = 0;
      this.heard = Date.now();
      this.probe = 0;
      this.handlers.status("open");
      this.send({ case: "hello", value: { clientBuild: CLIENT_BUILD } });
      clearInterval(this.ping);
      this.ping = setInterval(() => this.send({ case: "ping", value: { t: BigInt(Date.now()) } }), PING_MS);
      clearInterval(this.watch);
      this.watch = setInterval(() => {
        const now = Date.now();
        if (now - this.heard > SILENT_MS || (this.probe !== 0 && now - this.probe > PROBE_MS)) this.lost(ws);
      }, 1_000);
    };
    ws.onmessage = (e) => {
      this.heard = Date.now();
      this.probe = 0;
      if (!(e.data instanceof ArrayBuffer)) return;
      let m: ServerMessage;
      try {
        m = fromBinary(ServerMessageSchema, new Uint8Array(e.data));
      } catch {
        return;
      }
      this.observe(m);
      this.handlers.message(m);
    };
    ws.onclose = () => this.lost(ws);
  }

  // The link `ws` is gone, closed or silent: forget it (a late close of a silent one changes nothing) and
  // reconnect with backoff. A signed-out browser's upgrade is refused before the socket opens; the page
  // re-checks the session when the status stays "closed".
  private lost(ws: WebSocket): void {
    if (this.ws !== ws) return;
    clearInterval(this.ping);
    clearInterval(this.watch);
    this.ws = null;
    this.probe = 0;
    if (ws.readyState !== 3) ws.close();
    if (this.stopped) return;
    this.handlers.status("closed");
    const delay = Math.min(10_000, 500 * 2 ** this.retries++) * (0.75 + Math.random() / 2);
    clearTimeout(this.retry);
    this.retry = setTimeout(() => !this.stopped && this.open(), delay);
  }

  private observe(m: ServerMessage): void {
    const b = m.body;
    if (b.case === "tableAssigned") {
      this.gameId = b.value.gameId;
      proofs.seat(b.value.gameId, b.value.seat);
    } else if (b.case === "resync" && b.value.table) this.gameId = b.value.table.gameId;
    else if (b.case === "commitment") {
      const nonce = proofs.nonceFor(this.gameId, b.value);
      this.send({ case: "nonce", value: { gameId: this.gameId, commitment: b.value.commitment, nonce } });
    } else if (b.case === "nonceReceipt") {
      proofs.receipt(this.gameId, b.value.commitment, b.value.signature);
    } else if (b.case === "error" && (b.value.code === ErrorCode.SUPERSEDED || b.value.code === ErrorCode.NOT_AUTHENTICATED)) {
      this.stop(); // another tab took the seat, or the session ended: do not fight it
      this.handlers.status(b.value.code === ErrorCode.NOT_AUTHENTICATED ? "signed-out" : "superseded");
    }
  }

  private send(body: Body): void {
    if (this.ws?.readyState !== WebSocket.OPEN) return;
    const m = create(ClientMessageSchema, { protocolVersion: PROTOCOL_VERSION, body });
    this.ws.send(toBinary(ClientMessageSchema, m));
  }
}

function socketURL(): string {
  const proto = location.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${location.host}/ws`;
}
